import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '../../../lib/api-client.js';
import type { InviteInput, TeamUserPatch, BulkAssignmentItem } from '../../../lib/api-client.js';
import { teamKeys, machineKeys } from '../../../lib/query-keys.js';

export function useTeamUsers(shopId: string | null) {
  return useQuery({
    queryKey: shopId ? teamKeys.users(shopId) : ['team', 'none'],
    queryFn: () => getApiClient().listTeamUsers(shopId!),
    enabled: !!shopId,
    staleTime: 60_000,
  });
}

export function useInvite(shopId: string | null, inviteId: string | null) {
  return useQuery({
    queryKey: shopId && inviteId ? teamKeys.invite(shopId, inviteId) : ['team', 'invite', 'none'],
    queryFn: () => getApiClient().getInvite(shopId!, inviteId!),
    enabled: !!shopId && !!inviteId,
    retry: false,
  });
}

export function useMachineAssignments(shopId: string | null, machineId: string | null) {
  return useQuery({
    queryKey: shopId && machineId ? teamKeys.assignments(shopId, machineId) : ['team', 'assign', 'none'],
    queryFn: () => getApiClient().listMachineAssignments(shopId!, machineId!),
    enabled: !!shopId && !!machineId,
    staleTime: 30_000,
  });
}

export function useInviteUser(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteInput) => getApiClient().inviteUser(shopId!, input),
    onSuccess: () => {
      if (shopId) void qc.invalidateQueries({ queryKey: teamKeys.users(shopId) });
    },
  });
}

export function useUpdateTeamUser(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, patch }: { userId: string; patch: TeamUserPatch }) =>
      getApiClient().updateTeamUser(shopId!, userId, patch),
    onSuccess: () => {
      if (shopId) void qc.invalidateQueries({ queryKey: teamKeys.users(shopId) });
    },
  });
}

export function useRemoveTeamUser(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => getApiClient().removeTeamUser(shopId!, userId),
    onSuccess: () => {
      if (shopId) void qc.invalidateQueries({ queryKey: teamKeys.users(shopId) });
    },
  });
}

export function useBulkReplaceAssignments(shopId: string | null, machineId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignments: BulkAssignmentItem[]) =>
      getApiClient().bulkReplaceAssignments(shopId!, machineId!, assignments),
    onSuccess: () => {
      if (shopId && machineId) {
        void qc.invalidateQueries({ queryKey: teamKeys.assignments(shopId, machineId) });
      }
      if (shopId) void qc.invalidateQueries({ queryKey: machineKeys.list(shopId) });
    },
  });
}

export function useAcceptInvite(shopId: string | null, inviteId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => getApiClient().acceptInvite(shopId!, inviteId!),
    onSuccess: () => {
      if (shopId && inviteId) {
        void qc.invalidateQueries({ queryKey: teamKeys.invite(shopId, inviteId) });
      }
    },
  });
}
